import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Who owns each brief. One shared store (like useBriefStatus), so the queue,
 * the detail panel and Home agree. Changes are optimistic and roll back if
 * the server refuses them.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

let store = { items: {}, loaded: false, error: null };
const listeners = new Set();
const emit = (patch) => { store = { ...store, ...patch }; listeners.forEach((l) => l(store)); };
const setItems = (updates) => {
  const items = { ...store.items };
  Object.entries(updates).forEach(([id, v]) => { if (v && v.owner_id) items[id] = v; else delete items[id]; });
  emit({ items });
};

let inflight = null;
export function loadAssignments() {
  if (!inflight) {
    inflight = axios.get(`${API}/inbox/assignments`, { headers: headers() })
      .then((r) => emit({ items: r.data?.items || {}, loaded: true, error: null }))
      .catch((error) => emit({ loaded: true, error }))
      .finally(() => { inflight = null; });
  }
  return inflight;
}

export default function useAssignments() {
  const [snap, setSnap] = useState(store);
  useEffect(() => {
    listeners.add(setSnap);
    setSnap(store);
    if (!store.loaded) loadAssignments();
    return () => { listeners.delete(setSnap); };
  }, []);

  const getOwner = useCallback((id) => (id ? snap.items[id] || null : null), [snap.items]);

  const assign = useCallback(async (id, owner) => {
    const previous = store.items[id] || null;
    setItems({ [id]: owner ? { owner_id: 'pending', owner_email: null, assigned_at: new Date().toISOString() } : null });
    try {
      const res = await axios.put(`${API}/inbox/assignment/${encodeURIComponent(id)}`, { owner }, { headers: headers() });
      setItems({ [id]: res.data });
    } catch (err) {
      setItems({ [id]: previous });
      throw err;
    }
  }, []);

  const assignMany = useCallback(async (ids, owner) => {
    const previous = {};
    const optimistic = {};
    ids.forEach((id) => { previous[id] = store.items[id] || null; optimistic[id] = owner ? { owner_id: 'pending', assigned_at: new Date().toISOString() } : null; });
    setItems(optimistic);
    try {
      const res = await axios.post(`${API}/inbox/assignment/bulk`, { attack_ids: ids, owner }, { headers: headers() });
      loadAssignments();
      return res.data?.updated || 0;
    } catch (err) {
      setItems(previous);
      throw err;
    }
  }, []);

  return { getOwner, assign, assignMany, loaded: snap.loaded, error: snap.error, reload: loadAssignments };
}
